import PropTypes from 'prop-types';
import { useState } from 'react';
import { ChromePicker } from 'react-color';
import styled, { css } from 'styled-components';

import { Box } from '@hivebrite/design-system/Box';
import { FakeLink } from '@hivebrite/design-system/Link';

import ThemeProvider from 'hb-react/style/ThemeProvider';

import { colorToRgbaNotation } from './utils';

export const PICKER_POSITION = Object.freeze({
  TOP_LEFT: 'TOP_LEFT',
  TOP_RIGHT: 'TOP_RIGHT',
  BOTTOM_LEFT: 'BOTTOM_LEFT',
  BOTTOM_RIGHT: 'BOTTOM_RIGHT',
});

const InputColorPicker = ({
  title,
  resetText,
  color,
  defaultColor = '#323A45',
  disableAlpha = false,
  inputHtml,
  onChange,
  pickerPosition = PICKER_POSITION.BOTTOM_LEFT,
  rgbPreference = true,
}) => {
  const [displayColorPicker, setDisplayColorPicker] = useState(false);
  const [colorState, setColorState] = useState(color || defaultColor);
  const [stringifyColor, setStringifyColor] = useState(
    rgbPreference ? colorToRgbaNotation(colorState) : colorState,
  );

  const setColor = (value) => {
    setColorState(value?.rgb || value);
    setStringifyColor(
      rgbPreference ? colorToRgbaNotation(value?.rgb || value) : value.hex,
    );
  };

  const handleClick = () => {
    setDisplayColorPicker((prev) => !prev);
  };

  const handleClose = () => {
    setDisplayColorPicker(false);
  };

  const handleChange = (value) => {
    setColor(value);

    if (onChange) {
      onChange({
        rgb: value.rgb,
        hex: value.hex,
        rgbNotation: colorToRgbaNotation(value?.rgb || value),
      });
    }
  };

  const handleReset = () => {
    setColor(defaultColor);

    if (onChange) {
      onChange({
        hex: defaultColor,
      });
    }
  };

  return (
    <ThemeProvider theme="fo">
      <Box>
        <Container>
          <Preview
            $style={stringifyColor}
            onClick={handleClick}
            title={__('Pick a color')}
          />
          {displayColorPicker && (
            <Box>
              <Cover onClick={handleClose} />
              <PopoverPositioner $pickerPosition={pickerPosition}>
                <ChromePicker
                  color={colorState}
                  onChange={handleChange}
                  disableAlpha={disableAlpha}
                />
              </PopoverPositioner>
            </Box>
          )}
        </Container>
        {(title || resetText) && (
          <WrapperText data-testid="title-color-picker">
            {title && <Box>{title}</Box>}
            {resetText && (
              <FakeLink onClick={handleReset}>{resetText}</FakeLink>
            )}
          </WrapperText>
        )}
        <input
          {...inputHtml}
          type="hidden"
          className="hidden"
          data-testid="input-color-picker"
          value={stringifyColor}
        />
      </Box>
    </ThemeProvider>
  );
};

InputColorPicker.propTypes = {
  title: PropTypes.string,
  resetText: PropTypes.string,
  color: PropTypes.string,
  defaultColor: PropTypes.string,
  disableAlpha: PropTypes.bool,
  inputHtml: PropTypes.shape({
    name: PropTypes.string.isRequired,
  }),
  pickerPosition: PropTypes.oneOf(Object.keys(PICKER_POSITION)),
  onChange: PropTypes.func,
};

export default InputColorPicker;

const Container = styled(Box).attrs({
  display: 'inline-block',
  position: 'relative',
})``;

const Preview = styled(Box).attrs(({ $style }) => ({
  width: 30,
  height: 30,
  bg: $style,
  cursor: 'pointer',
}))`
  border: 1px ${({ theme }) => theme.colors.dove} solid;
  border-radius: 15%;
`;

const Cover = styled(Box)`
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
`;

const WrapperText = styled(Box).attrs({
  pl: 3,
  display: 'inline-block',
  verticalAlign: 'middle',
})``;

const PopoverPositioner = styled(Box).attrs({
  position: 'absolute',
})`
  z-index: 120;

  ${({ $pickerPosition }) => {
    switch ($pickerPosition) {
      case PICKER_POSITION.BOTTOM_LEFT:
        return css`
          left: 0;
          margin-top: 4px;
          top: 100%;
        `;
      case PICKER_POSITION.BOTTOM_RIGHT:
        return css`
          right: 0;
          margin-top: 4px;
          top: 100%;
        `;
      case PICKER_POSITION.TOP_RIGHT:
        return css`
          right: 0;
          margin-bottom: 6px;
          bottom: 100%;
        `;
      case PICKER_POSITION.TOP_LEFT:
      default:
        return css`
          left: 0;
          margin-bottom: 6px;
          bottom: 100%;
        `;
    }
  }}
`;
